import { useState, useMemo } from 'react'
import Input from '../../Shared/Input';
import Selectbox, { option } from '../../Shared/SelectBox';
import StudentProgressRow from '../../Dashboard/Students/StudentProgressRow';
import { StudentProgress } from '../../../api/Students/StudentCourseProgress';

interface StudentProgressTableProps {
    data?: StudentProgress[];
    prevButtonColor?: string;
    nextButtonColor?: string;
    disabledButtonColor?: string;
}

const sortOptions: option[] = [
    { name: 'Highest Progress', code: 'highest' },
    { name: 'Lowest Progress', code: 'lowest' }
];

const StudentProgressTable = ({
    data,
    prevButtonColor = 'bg-blue-500 text-white',
    nextButtonColor = 'bg-blue-500 text-white',
    disabledButtonColor = 'bg-gray-300',
}: StudentProgressTableProps) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedSort, setSelectedSort] = useState<option | null>(null);
    const itemsPerPage = 10;

    const filteredData = useMemo(() => {
        if (!data) return [];
        return data.filter(student => 
            student.studentName.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [data, searchQuery]);

    const sortedData = useMemo(() => {
        if (!filteredData) return [];
        if (selectedSort?.code === 'highest') {
            return [...filteredData].sort((a, b) => b.percentage - a.percentage);
        } else if (selectedSort?.code === 'lowest') {
            return [...filteredData].sort((a, b) => a.percentage - b.percentage);
        }
        return filteredData;
    }, [filteredData, selectedSort]);

    const currentData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return sortedData.slice(startIndex, startIndex + itemsPerPage);
    }, [sortedData, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    return (
        <div>
            <div className="mb-8 flex items-center justify-end gap-5 max-w-2xl ml-auto">
                <Input
                    name="search"
                    placeholder="Search by first name"
                    className='w-full'
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    inputStyles="!h-12"
                />
                <Selectbox
                titleStyles='!mb-2 md:text-base'
                    selectOptions={sortOptions}
                    selected={selectedSort}
                    setSelected={setSelectedSort}
                    placeholder="Select sorting option"
                    className="w-48 !h-12"
                />
            </div>
            <div className="overflow-x-auto scrollbar-thumb-blue scrollbar-thumb-rounded scrollbar-track-blue-lighter scrollbar-w-2 scrolling-touch">
                <table className="table-auto w-full">
                    <thead className="text-lg capitalize">
                        <tr className="bg-[#F1F4F9] rounded-t-lg">
                            <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                                <div className="text-left text-sm">Full Name</div>
                            </th>
                            <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                                <div className="text-left text-sm">Student Progress</div>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="text-sm">
                        {currentData.map((student: StudentProgress) => (
                            <StudentProgressRow
                                key={student.studentId}
                                fullName={student.studentName}
                                studentId={student.studentId}
                                progress={student.percentage}
                                videosWatched={student.videosWatched}
                                totalVideos={student.totalVideos}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="flex justify-between mt-4">
                <button
                    className={`px-4 py-2 ${currentPage === 1 ? disabledButtonColor : prevButtonColor} rounded`}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                >
                    Previous
                </button>
                <span>Page {currentPage} of {totalPages}</span>
                <button
                    className={`px-4 py-2 ${currentPage === totalPages ? disabledButtonColor : nextButtonColor} rounded`}
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                >
                    Next
                </button>
            </div>
            {filteredData.length === 0 && <p>No data available</p>}
        </div>
    );
};

export default StudentProgressTable;
